"use client";

import dynamic from "next/dynamic";
import Link from "next/link";
import { Component, useCallback, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from "react";
import { useAppearance } from "@/components/site/AppearanceProvider";
import { PieceArt } from "@/components/board/PieceArt";
import { BOARDS } from "@/lib/appearance";
import { JOURNEY_MOVES, JOURNEY_PIECES, chapterAt, currentMove, scrollProgress } from "@/lib/home-journey";
import { useEngaged } from "@/lib/engagement";
import "./journey.css";

const JourneyScene = dynamic(() => import("./JourneyScene"), { ssr: false });

let webgl2: boolean | undefined;
function hasWebGL2() {
  if (webgl2 === undefined) {
    try {
      const gl = document.createElement("canvas").getContext("webgl2");
      webgl2 = !!gl;
      gl?.getExtension("WEBGL_lose_context")?.loseContext();
    } catch { webgl2 = false; }
  }
  return webgl2;
}
const subscribeNever = () => () => {};

class SceneBoundary extends Component<{ children: ReactNode; onFailure: () => void }, { failed: boolean }> {
  state = { failed: false };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch() { this.props.onFailure(); }
  render() { return this.state.failed ? null : this.props.children; }
}

const CHAPTERS = [
  { label: "Discover", eyebrow: "", first: "Every move", second: "tells a story.", description: "Play, learn, and explore the world of chess.", link: "/play", action: "Play chess" },
  { label: "Pieces", eyebrow: "Meet the pieces", first: "Great things", second: "take shape.", description: "See how every piece moves and works together.", link: "/lessons", action: "Explore the lessons" },
  { label: "Openings", eyebrow: "The Italian Game", first: "Start with", second: "a plan.", description: "Follow a classic opening, one move at a time.", link: "/openings", action: "Explore openings" },
  { label: "Play", eyebrow: "", first: "The next move", second: "is yours.", description: "Choose your board. Find your level. Enjoy the game.", link: "/play", action: "Play chess" },
] as const;

/** Semantic, lightweight poster remains visible until the real-time scene is ready. */
function BoardPoster() {
  const { appearance } = useAppearance();
  const board = BOARDS[appearance.board];
  return <div className="journey-poster"><div className="journey-poster-board" style={{ borderColor: board.frame }}>
    {Array.from({ length: 64 }, (_, i) => {
      const square = `${String.fromCharCode(97 + i % 8)}${8 - Math.floor(i / 8)}`;
      const piece = JOURNEY_PIECES.find((p) => p.square === square);
      return <div key={i} style={{ backgroundColor: (i + Math.floor(i / 8)) % 2 ? board.dark : board.light }}>{piece && <PieceArt type={piece.type} color={piece.color} finish={appearance.pieces} style={appearance.style} />}</div>;
    })}
  </div></div>;
}

export function HomeJourney() {
  const { reducedMotion } = useAppearance();
  const root = useRef<HTMLElement>(null);
  const stage = useRef<HTMLDivElement>(null);
  const progress = useRef(0);
  const [chapter, setChapter] = useState(0);
  const [move, setMove] = useState(-1);
  const [active, setActive] = useState(true);
  const [ready, setReady] = useState(false);
  const [sceneFailed, setSceneFailed] = useState(false);
  // null during server render and hydration; the scene mounts once the client answers.
  // Creating a WebGL context is itself slow without a GPU, so the probe waits for engagement too.
  const engaged = useEngaged();
  const supported = useSyncExternalStore(subscribeNever, () => engaged ? hasWebGL2() : null, () => null);
  const onReady = useCallback(() => setReady(true), []);
  const onFailure = useCallback(() => { setSceneFailed(true); setReady(false); }, []);
  const failed = sceneFailed || supported === false;
  const staticMode = reducedMotion || failed;

  useEffect(() => {
    const element = root.current, sticky = stage.current;
    if (!element || !sticky) return;
    let frame = 0;
    const measure = () => {
      frame = 0;
      const rect = element.getBoundingClientRect();
      const header = parseFloat(getComputedStyle(sticky).top) || 0;
      const next = staticMode ? 0 : scrollProgress(rect.top, rect.height, sticky.offsetHeight, header);
      progress.current = next;
      sticky.style.setProperty("--journey-progress", String(next));
      sticky.style.setProperty("--scene-progress", String(next));
      setChapter(chapterAt(next)); setMove(currentMove(next));
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
    const observer = new IntersectionObserver(([entry]) => setActive(entry.isIntersecting && !document.hidden), { rootMargin: "100px" });
    observer.observe(element);
    const visibility = () => { const r = element.getBoundingClientRect(); setActive(!document.hidden && r.bottom > 0 && r.top < innerHeight); };
    const resize = new ResizeObserver(schedule); resize.observe(element); resize.observe(sticky);
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    document.addEventListener("visibilitychange", visibility);
    measure();
    return () => { cancelAnimationFrame(frame); observer.disconnect(); resize.disconnect(); window.removeEventListener("scroll", schedule); window.removeEventListener("resize", schedule); document.removeEventListener("visibilitychange", visibility); };
  }, [staticMode]);


  return <section ref={root} className={`home-journey${staticMode ? " journey-static" : ""}`} aria-label="A cinematic introduction to Ponderchess">
    <div ref={stage} className="journey-stage" data-chapter={chapter}>
      <div className="journey-atmosphere" aria-hidden="true"><div className="journey-window-light" /></div>
      <div className={`journey-visual${ready ? " scene-ready" : ""}`} aria-hidden="true">
        <div className="journey-fallback"><BoardPoster /></div>
        {supported && !failed && <SceneBoundary onFailure={onFailure}><JourneyScene progress={progress} active={active} reducedMotion={reducedMotion} onReady={onReady} onFailure={onFailure} /></SceneBoundary>}
      </div>
      <div className="journey-copy-stack">
        {CHAPTERS.map((item, i) => <article key={item.label} className="journey-copy" data-active={chapter === i} aria-hidden={chapter !== i} inert={chapter !== i}>
          {item.eyebrow && <p className="eyebrow journey-copy-eyebrow">{item.eyebrow}</p>}
          {i === 0 ? <h1><span><span>{item.first}</span></span><span><em>{item.second}</em></span></h1> : <h2><span><span>{item.first}</span></span><span><em>{item.second}</em></span></h2>}
          <p className="journey-description">{item.description}</p>
          <div className="journey-actions"><Link href={item.link} className="button-primary">{item.action} <span aria-hidden="true">↗</span></Link>{i === 0 && <Link href="/learn" className="journey-text-link">Start learning <span aria-hidden="true">→</span></Link>}</div>
          {i === 2 && <div className="journey-moves" aria-live="polite"><span>Current move</span><strong>{move < 0 ? "Starting position" : `${Math.floor(move / 2) + 1}${move % 2 === 0 ? "." : "…"} ${JOURNEY_MOVES[move].san}`}</strong></div>}
        </article>)}
      </div>
      <div className="journey-progress" aria-hidden="true"><span /></div>
    </div>
  </section>;
}

/** Enhances existing server-rendered sections without hiding content before JS loads. */
export function HomeReveals({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const { reducedMotion } = useAppearance();
  useEffect(() => {
    if (!ref.current || reducedMotion) return;
    const nodes = ref.current.querySelectorAll<HTMLElement>(".section-intro, .atlas-card, .atlas-feature, .collection-link");
    const observer = new IntersectionObserver((entries) => entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add("has-arrived"); observer.unobserve(entry.target); }
    }), { threshold: .12 });
    nodes.forEach((node, i) => { node.style.setProperty("--reveal-delay", `${i % 4 * 70}ms`); node.classList.add("journey-reveal"); observer.observe(node); });
    return () => { observer.disconnect(); nodes.forEach((node) => node.classList.remove("journey-reveal")); };
  }, [reducedMotion]);
  return <div ref={ref} className="home-discover">{children}</div>;
}
